import type { Dashboardv99990101Api } from '@sequencehq/api/dashboard/v99990101'

import type { useCreateAggregatedLineItemGroups } from 'CreditNotes/hooks/useCreateAggregatedLineItemGroups'
import { getCreditNoteBillingPeriod } from 'CreditNotes/utils/getCreditNoteBillingPeriod.ts'
import { Invoice } from 'Invoices/types'

import { CreditNoteLineItemRow } from './CreditNoteLineItemTable'

const CREDIT_NOTE_GROUP_TITLE = 'Credit note'

/**
 * The React-bound callables this module needs, gathered by the calling hook
 * (useMutation / useCreateAggregatedLineItemGroups) and injected so the
 * creation sequence itself stays a plain, unit-testable function.
 */
export type CreateCreditNoteEffects = {
  postCreditNote: (
    params: Dashboardv99990101Api.PostCreditNote.Params
  ) => Promise<{ id: string } | null | undefined>
  createLineItemGroups: ReturnType<typeof useCreateAggregatedLineItemGroups>
}

export type CreateCreditNoteParams = {
  accountingDate: string
  invoice: Pick<
    Invoice,
    'id' | 'currency' | 'customerId' | 'customerTaxId' | 'billingPeriod'
  >
  rows: CreditNoteLineItemRow[]
}

/**
 * Shared first half of both drawer submission flows ("Issue and send" and
 * "Create as draft"): create a draft credit note against the invoice and
 * attach the checked rows as its line items and groups. Returns the credit
 * note id, or null when there is nothing to create. No UX side effects;
 * callers own toasts, navigation, and error handling (API failures throw).
 */
export const createCreditNoteWithLineItems = async (
  { postCreditNote, createLineItemGroups }: CreateCreditNoteEffects,
  { accountingDate, invoice, rows }: CreateCreditNoteParams
): Promise<string | null> => {
  const selectedRows = rows.filter(row => row.checked)

  if (!selectedRows.length) {
    return null
  }

  const { billingPeriodStart, billingPeriodEnd } = getCreditNoteBillingPeriod(
    invoice.billingPeriod
  )

  const creditNote = await postCreditNote({
    body: {
      currency: invoice.currency,
      customerId: invoice.customerId,
      invoiceId: invoice.id,
      accountingDate,
      billingPeriodStart,
      billingPeriodEnd,
      customerTaxId: invoice.customerTaxId
    }
  })

  if (!creditNote) {
    return null
  }

  await createLineItemGroups(creditNote.id, [
    {
      title: CREDIT_NOTE_GROUP_TITLE,
      lineItems: selectedRows.map(toAggregatedLineItem)
    }
  ])

  return creditNote.id
}

const toAggregatedLineItem = (row: CreditNoteLineItemRow) => ({
  // Source invoice line item id, or undefined for a manually added line item.
  id: row.lineItemId,
  productId: row.productId,
  taxRate: row.taxRate,
  groupId: row.groupId,
  // Required by the aggregated-group shape but ignored by the create request;
  // the backend derives net/gross totals from rate × quantity.
  grossTotal: row.price,
  quantity: row.qty,
  title: row.title,
  rate: row.rate,
  // Populated only for manually added line items.
  description: row.description,
  rateDisplay: row.rateDisplay,
  revenueRecognitionMethod: row.revenueRecognitionMethod,
  servicePeriod: row.servicePeriod
    ? {
        startDate: row.servicePeriod.start,
        endDate: row.servicePeriod.endInclusive
      }
    : undefined
})
